/**
 * BUYKEYS.TSX - Compact Key Purchase Component
 */

import { getEthereum } from '../wallet/activeProvider';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { getGameContract, getFRAMEContract } from '../utils/contract';
import { ethers } from 'ethers';
import { FACTIONS, getFactionByContractId } from '../config/factions';
import { useDemoMode } from './DemoMode';
import { THALIAInline } from './THALIAIcon';
import { SocialSharing } from './SocialSharing';
import FactionChoiceModal, { FACTION_CHOICE_KEY } from './FactionChoiceModal';
import SharePrompt, { type ShareInfo } from './SharePrompt';
import { parseKeyPurchased } from '../utils/playerHistory';
import { recordLocalFactionBuy } from './FactionLeaderboard';
import { BuyKeysWarnings } from './BuyKeysWarnings';
import { BUY_LOCK_SECONDS } from '../config/onchainFeatures';
import ReaperKey from './ReaperKey';

interface BuyKeysProps {
  account: string | null;
  currentPrice: bigint;
  onPurchase?: () => void;
  referrerFromUrl?: string | null;
  // Game state flags for contextual warnings
  fomoSurgeActive?: boolean;
  flashCrashActive?: boolean;
  isAssassinMode?: boolean;
  timeRemaining?: number;
  /** current leader, used to detect "you took the lead" */
  lastBuyer?: string;
  /** Phase 1.1: no keys sold yet (contract blocks Reaper keys) */
  emptyRound?: boolean;
}

const SMALL_KEY_AMOUNTS = [1, 2, 3, 4, 5];
const QUICK_KEY_AMOUNTS = [10, 50, 100, 500];

/** Exact amount with thousands separators, no float rounding (bigint-safe). */
function exactFrame(wei: bigint): string {
  const [int, dec = ''] = ethers.formatEther(wei).split('.');
  const d = dec.replace(/0+$/, '').slice(0, 6);
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (d ? '.' + d : '');
}

export default function BuyKeys({ 
  account, 
  currentPrice, 
  onPurchase, 
  referrerFromUrl,
  fomoSurgeActive = false,
  flashCrashActive = false,
  isAssassinMode = false,
  timeRemaining = -1,
  lastBuyer = '',
  emptyRound = false,
}: BuyKeysProps) {
  // Demo mode
  const demoMode = useDemoMode();
  
  // State
  const [pawAmount, setPawAmount] = useState<string>('');
  const [selectedFaction, setSelectedFaction] = useState<number>(() => Number(localStorage.getItem(FACTION_CHOICE_KEY)) || 1); // Contract ID 1 = Raven
  const [factionConfirmed, setFactionConfirmed] = useState<boolean>(() => !!localStorage.getItem(FACTION_CHOICE_KEY));
  const [showFactionChoice, setShowFactionChoice] = useState(false);
  const [pendingBuy, setPendingBuy] = useState(false);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const chooseFaction = (id: number) => {
    setSelectedFaction(id);
    setFactionConfirmed(true);
    try { localStorage.setItem(FACTION_CHOICE_KEY, String(id)); } catch { /* ignore */ }
  };
  const [referrer, setReferrer] = useState<string>('');
  const [purchaseMessage, setPurchaseMessage] = useState<string>('');
  const [showMessageInput, setShowMessageInput] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [allowance, setAllowance] = useState<bigint>(0n);
  const [lockedFaction, setLockedFactionState] = useState<number | null>(() => {
    // Restore from sessionStorage to survive component remounts (tab switches)
    const stored = sessionStorage.getItem('nt3d_locked_faction');
    return stored ? Number(stored) : null;
  });
  const [cascadeLevel, setCascadeLevel] = useState<number>(0); // Was comboCount
  const [approveUnlimited, setApproveUnlimited] = useState(false);
  const [ethBalance, setEthBalance] = useState<bigint | null>(null);

  // Wrapper that also persists to sessionStorage
  const setLockedFaction = useCallback((factionId: number | null) => {
    setLockedFactionState(factionId);
    if (factionId !== null) {
      sessionStorage.setItem('nt3d_locked_faction', String(factionId));
    } else {
      sessionStorage.removeItem('nt3d_locked_faction');
    }
  }, []);
  const [lastPurchaseData, setLastPurchaseData] = useState<{ keys: string; amount: string } | null>(null);


  // Use referrer from URL if provided
  useEffect(() => {
    if (referrerFromUrl && !referrer) {
      setReferrer(referrerFromUrl);
    }
  }, [referrerFromUrl, referrer]);

  // Fetch allowance and player data
  const fetchData = useCallback(async () => {
    if (!account) return;
    try {
      const [gameContract, frameContract] = await Promise.all([getGameContract(), getFRAMEContract()]);
      if (!frameContract || !gameContract) return;
      
      const gameAddress = await gameContract.getAddress();
      const currentAllowance = await frameContract.allowance(account, gameAddress);
      
      // Exact approvals shrink after each buy, so always take the chain's value.
      setAllowance(currentAllowance);

      // Get player's locked faction and cascade
      try {
        const roundData = await gameContract.currentRound();
        const roundId = roundData[0];
        const playerData = await gameContract.playerRoundData(roundId, account);
        const keysOwned = playerData[0];
        const factionId = Number(playerData[1]);
        const cascade = Number(playerData[4] || 0);
        
        if (keysOwned > 0n && factionId > 0) {
          setLockedFaction(factionId);
          setSelectedFaction(factionId);
        } else if (keysOwned === 0n) {
          // Only clear the lock if there's no recently-set persisted value
          // This prevents a race condition where fetchData runs after purchase
          // but the blockchain hasn't indexed the new keys yet
          const persisted = sessionStorage.getItem('nt3d_locked_faction');
          if (!persisted) {
            setLockedFaction(null);
          }
        }
        setCascadeLevel(cascade);
      } catch (e) {
        console.warn('[BuyKeys] Could not fetch player data:', e);
        // Don't clear lockedFaction on error — keep any persisted value
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [account]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch ETH balance for gas warnings
  useEffect(() => {
    const fetchEthBalance = async () => {
      if (!account || !getEthereum()) {
        setEthBalance(null);
        return;
      }
      try {
        const provider = new ethers.BrowserProvider(getEthereum());
        const balance = await provider.getBalance(account);
        setEthBalance(balance);
      } catch (e) {
        console.warn('[BuyKeys] Could not fetch ETH balance:', e);
      }
    };
    fetchEthBalance();
    const interval = setInterval(fetchEthBalance, 30000); // Update every 30s
    return () => clearInterval(interval);
  }, [account]);

  // Calculations
  const pawAmountBigInt = useMemo(() => {
    try {
      const parsed = parseFloat(pawAmount);
      if (isNaN(parsed) || parsed <= 0) return 0n;
      return ethers.parseEther(pawAmount);
    } catch { return 0n; }
  }, [pawAmount]);

  const estimatedKeys = useMemo(() => {
    if (currentPrice <= 0n || pawAmountBigInt <= 0n) return { post: 0n, bonus: false };
    const keys = pawAmountBigInt / currentPrice;
    const bonus = keys >= 100n;
    return { post: bonus ? keys + (keys / 20n) : keys, bonus };
  }, [pawAmountBigInt, currentPrice]);

  const needsApproval = allowance < pawAmountBigInt;

  // Parse transaction errors into user-friendly messages
  const parseTransactionError = (error: any): string => {
    const errorString = error?.reason || error?.message || String(error);
    const errorLower = errorString.toLowerCase();
    
    // Common revert reasons
    if (errorLower.includes('insufficient') && errorLower.includes('balance')) {
      return 'Insufficient FRAME balance. Check your wallet balance.';
    }
    if (errorLower.includes('exceeds allowance') || errorLower.includes('not approved')) {
      return 'The game is not allowed to use enough test FRAME yet. Tap "Allow test FRAME" first.';
    }
    if (errorLower.includes('round ended') || errorLower.includes('round not active')) {
      return 'Round has ended. Wait for a new round to start.';
    }
    if (errorLower.includes('faction') && errorLower.includes('locked')) {
      return 'Wrong faction selected. You must buy with your locked faction.';
    }
    if (errorLower.includes('user rejected') || errorLower.includes('user denied')) {
      return 'Transaction cancelled by user.';
    }
    if (errorLower.includes('gas') || errorLower.includes('intrinsic gas too low')) {
      return 'Insufficient ETH for gas fees. Add more ETH to your wallet.';
    }
    if (errorLower.includes('nonce')) {
      return 'Transaction nonce error. Try resetting your wallet or wait a moment.';
    }
    if (errorLower.includes('timeout') || errorLower.includes('network')) {
      return 'Network error. Check your connection and try again.';
    }
    if (errorLower.includes('underpriced')) {
      return 'Gas price too low. Try increasing gas in your wallet.';
    }
    
    // Return original if no match
    return error?.reason || 'Transaction failed. Please try again.';
  };

  // Handlers
  // Approve the exact amount needed (default) or unlimited when the player opts in.
  const handleApprove = async (needed?: bigint) => {
    if (!account) return;
    const exact = needed && needed > 0n ? needed : pawAmountBigInt + pawAmountBigInt / 50n;
    const amount = approveUnlimited ? ethers.MaxUint256 : exact;
    setIsApproving(true);
    try {
      const frameContract = await getFRAMEContract(true);
      if (!frameContract) throw new Error('FRAME contract not available');
      const gameContract = await getGameContract();
      if (!gameContract) throw new Error('Game contract not available');
      const gameAddress = await gameContract.getAddress();
      
      const tx = await frameContract.approve(gameAddress, amount);
      toast.loading('Allowing the game to use your test FRAME…', { id: 'approve' });
      await tx.wait();
      toast.success(approveUnlimited ? 'Done. The game can use your test FRAME until you revoke it.' : 'Done. Now confirm the key buy.', { id: 'approve' });
      
      // The read RPC can lag behind; use the amount we just approved
      setAllowance(amount);
      
      // Also trigger background refresh in case we need fresh player data
      setTimeout(() => fetchData(), 2000);
    } catch (error: any) {
      console.error('Approve failed:', error);
      toast.error(parseTransactionError(error), { id: 'approve' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleBuy = async () => {
    if (lockedFaction === null && !factionConfirmed) {
      setShowFactionChoice(true);
      return;
    }
    const tookLead = !account || !lastBuyer || lastBuyer.toLowerCase() !== account.toLowerCase();
    if (pawAmountBigInt <= 0n) {
      toast.error(demoMode.isDemo ? 'Pick a key amount first' : 'Enter a FRAME amount first');
      return;
    }
    
    // Demo mode: use simulated purchase
    if (demoMode.isDemo) {
      setIsLoading(true);
      try {
        const result = demoMode.buyKeys(pawAmountBigInt, selectedFaction);
        if (result.success) {
          toast.success(`🎮 Demo: Acquired ~${Number(result.keys)} keys! Timer +${Number(result.keys) * 30}s`, { id: 'buy' });
          setLastPurchaseData({ keys: result.keys.toString(), amount: ethers.formatEther(pawAmountBigInt) });
          recordLocalFactionBuy(selectedFaction, Number(result.keys));
          setShareInfo({ account, keys: result.keys.toString(), amount: ethers.formatEther(pawAmountBigInt), faction: lockedFaction || selectedFaction, tookLead });
          setPawAmount('');
          setPurchaseMessage('');
          onPurchase?.();
        } else {
          toast.error(`🎮 Demo: ${result.message}`, { id: 'buy' });
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Real mode: require account
    if (!account) return;
    setIsLoading(true);
    try {
      const gameContract = await getGameContract(true);
      if (!gameContract) throw new Error('Game contract not available');
      
      const refAddress = referrer && ethers.isAddress(referrer) ? referrer : ethers.ZeroAddress;
      
      toast.loading('Acquiring keys...', { id: 'buy' });
      const tx = await gameContract.buyKeys(pawAmountBigInt, selectedFaction, refAddress, purchaseMessage);
      const receipt = await tx.wait();

      // Read what the contract actually recorded (keys, faction, amount) from KeyPurchased.
      const bought = parseKeyPurchased(receipt, account);
      const recordedFaction = bought?.faction || selectedFaction;

      // Immediately lock faction after purchase
      setLockedFaction(recordedFaction);
      
      const keysAcquired = (bought ? Number(bought.keys) : Number(estimatedKeys.post)).toLocaleString();
      toast.success(`Acquired ~${keysAcquired} keys!`, { id: 'buy' });
      setLastPurchaseData({ keys: keysAcquired, amount: ethers.formatEther(pawAmountBigInt) });
      recordLocalFactionBuy(recordedFaction, bought ? Number(bought.keys) : Number(estimatedKeys.post));
      setShareInfo({
        account,
        keys: keysAcquired,
        amount: ethers.formatEther(bought?.spent ?? pawAmountBigInt),
        faction: recordedFaction,
        tookLead,
      });
      setPawAmount('');
      setPurchaseMessage('');
      onPurchase?.();
      // Delay fetchData to let blockchain state settle — immediate fetch can race
      // and return stale data (keysOwned=0) which would clear the faction lock
      setTimeout(() => fetchData(), 3000);
    } catch (error: any) {
      console.error('Buy failed:', error);
      toast.error(parseTransactionError(error), { id: 'buy' });
    } finally {
      setIsLoading(false);
    }
  };

  // Continue the buy once the faction has been chosen
  useEffect(() => {
    if (pendingBuy && factionConfirmed) { setPendingBuy(false); handleBuy(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingBuy, factionConfirmed]);

  // Onboarding: prefill a single key
  useEffect(() => {
    const h = () => {
      const price = demoMode.isDemo && demoMode.demo.keyPrice > 0n ? demoMode.demo.keyPrice : currentPrice;
      if (price > 0n) setPawAmount(ethers.formatEther((price * 105n) / 100n));
      if (lockedFaction === null && !factionConfirmed) setShowFactionChoice(true);
    };
    window.addEventListener('nt3d:prefill-first-key', h);
    return () => window.removeEventListener('nt3d:prefill-first-key', h);
  }, [currentPrice, demoMode.isDemo, demoMode.demo.keyPrice, lockedFaction, factionConfirmed]);

  return (
    <motion.div
      className="relative glass-panel-3d"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <FactionChoiceModal open={showFactionChoice} current={selectedFaction} onClose={() => setShowFactionChoice(false)} onChoose={(id) => { chooseFaction(id); setShowFactionChoice(false); setPendingBuy(pawAmountBigInt > 0n); }} />
      <SharePrompt info={shareInfo} onClose={() => setShareInfo(null)} />
      <div className="p-4 md:p-5 relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neon-magenta/20 to-neon-purple/20 flex items-center justify-center border border-neon-magenta/30">
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-neon-magenta">
              <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 12l8 8M16 20l2-2M18 18l2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="8" cy="8" r="2" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h2 className="font-orbitron text-lg text-neon-magenta">ACQUIRE KEYS</h2>
            <p className="text-xs text-gray-500 font-rajdhani">Delay the Singularity • Fight THALIA</p>
            {demoMode.isDemo && (
              <p className="text-xs text-yellow-400 font-mono mt-1">
                Demo · {demoMode.demo.keys.toString()} keys · {demoMode.demo.timeRemaining}s left · {Number(ethers.formatEther(demoMode.demo.balance)).toLocaleString(undefined, { maximumFractionDigits: 0 })} FRAME
              </p>
            )}
          </div>
        </div>
        
        {/* Input + Preview Row */}
        <div className="flex flex-col gap-4 mb-4">
          {/* FRAME Input */}
          <div>
            <label htmlFor="nt3d-frame-amount" className="block text-xs uppercase tracking-widest text-neon-magenta/80 font-mono mb-1.5 text-center">FRAME Amount</label>
            <div className="flex items-center gap-2 w-full min-w-0 pl-3 pr-3 rounded-xl bg-void-black/50 border border-neon-magenta/30 focus-within:border-neon-magenta focus-within:shadow-[0_0_20px_rgba(255,0,128,0.3)] transition-all">
              <input
                id="nt3d-frame-amount"
                type="text"
                inputMode="decimal"
                value={pawAmount}
                onChange={(e) => {
                  // Allow only numbers and dots (no commas in input - display only)
                  const raw = e.target.value.replace(/[^0-9.]/g, '');
                  setPawAmount(raw);
                }}
                className={`flex-1 w-0 min-w-0 py-3 bg-transparent border-0 text-white ${pawAmount.length > 16 ? 'text-xs' : pawAmount.length > 11 ? 'text-sm' : 'text-xl'} font-orbitron text-center focus:outline-none`}
                placeholder="0"
              />
              <span className="flex-shrink-0 text-sm text-neon-magenta/70 font-mono font-bold pointer-events-none">FRAME</span>
            </div>
            {/* Formatted display */}
            {pawAmountBigInt > 0n && (
              <div className="text-center text-xs text-neon-magenta/70 mt-1 font-mono break-all min-w-0" data-testid="frame-needed">
                {exactFrame(pawAmountBigInt)} test FRAME
                <span className="text-neon-magenta/50"> (≈ {Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(Number(ethers.formatEther(pawAmountBigInt)))})</span>
              </div>
            )}
            {/* Quick key amounts - small */}
            <div className="grid grid-cols-5 gap-1.5 mt-2">
              {SMALL_KEY_AMOUNTS.map((keyCount) => {
                return (
                  <motion.button
                    key={keyCount}
                    onClick={async () => {
                      // Calculate accurate FRAME cost for desired key count using bonding curve
                      try {
                        if (demoMode.isDemo) {
                          const price = demoMode.demo.keyPrice > 0n ? demoMode.demo.keyPrice : currentPrice;
                          if (price > 0n) setPawAmount(ethers.formatEther(BigInt(keyCount) * price));
                          return;
                        }
                        const gameContract = await getGameContract();
                        if (!gameContract) {
                          if (currentPrice > 0n) setPawAmount(ethers.formatEther(BigInt(keyCount) * currentPrice));
                          return;
                        }
                        
                        // Get current round data for totalKeysSold
                        const roundData = await gameContract.currentRound();
                        const totalKeysSold = roundData.totalKeysSold || 0n;
                        
                        // Get base price and increment from contract
                        const basePrice = await gameContract.baseKeyPrice();
                        const increment = await gameContract.keyPriceIncrement();
                        
                        // Calculate total cost using bonding curve formula
                        const n = BigInt(keyCount);
                        const totalCost = n * basePrice + increment * (n * totalKeysSold + (n * (n - 1n)) / 2n);
                        
                        // Add 2% buffer for price movement during tx
                        const costWithBuffer = (totalCost * 102n) / 100n;
                        
                        setPawAmount(ethers.formatEther(costWithBuffer));
                      } catch (e) {
                        console.error('Failed to calculate key cost:', e);
                        // Fallback to simple calculation
                        if (currentPrice > 0n) {
                          const simpleCost = BigInt(keyCount) * currentPrice;
                          setPawAmount(ethers.formatEther(simpleCost));
                        }
                      }
                    }}
                    className="flex-1 min-w-0 inline-flex items-center justify-center gap-0.5 py-1.5 rounded-lg border text-xs font-mono transition-all bg-void-black/50 border-gray-700 text-gray-400 hover:border-neon-cyan/50 hover:bg-neon-cyan/10 hover:text-neon-cyan"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {keyCount} <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  </motion.button>
                );
              })}
            </div>
            {/* Quick key amounts - large */}
            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
              {QUICK_KEY_AMOUNTS.map((keyCount) => {
                return (
                  <motion.button
                    key={keyCount}
                    onClick={async () => {
                      // Calculate accurate FRAME cost for desired key count using bonding curve
                      try {
                        if (demoMode.isDemo) {
                          const price = demoMode.demo.keyPrice > 0n ? demoMode.demo.keyPrice : currentPrice;
                          if (price > 0n) setPawAmount(ethers.formatEther(BigInt(keyCount) * price));
                          return;
                        }
                        const gameContract = await getGameContract();
                        if (!gameContract) {
                          if (currentPrice > 0n) setPawAmount(ethers.formatEther(BigInt(keyCount) * currentPrice));
                          return;
                        }
                        
                        // Get current round data for totalKeysSold
                        const roundData = await gameContract.currentRound();
                        const totalKeysSold = roundData.totalKeysSold || 0n;
                        
                        // Get base price and increment from contract
                        const basePrice = await gameContract.baseKeyPrice();
                        const increment = await gameContract.keyPriceIncrement();
                        
                        // Calculate total cost using bonding curve formula
                        // Cost = sum of (basePrice + (totalKeysSold + i) * increment) for i = 0 to keyCount-1
                        // = keyCount * basePrice + increment * sum(totalKeysSold + i) for i = 0 to keyCount-1
                        // = keyCount * basePrice + increment * (keyCount * totalKeysSold + keyCount*(keyCount-1)/2)
                        const n = BigInt(keyCount);
                        const totalCost = n * basePrice + increment * (n * totalKeysSold + (n * (n - 1n)) / 2n);
                        
                        // Add 2% buffer for price movement during tx
                        const costWithBuffer = (totalCost * 102n) / 100n;
                        
                        setPawAmount(ethers.formatEther(costWithBuffer));
                      } catch (e) {
                        console.error('Failed to calculate key cost:', e);
                        // Fallback to simple calculation
                        if (currentPrice > 0n) {
                          const simpleCost = BigInt(keyCount) * currentPrice;
                          setPawAmount(ethers.formatEther(simpleCost));
                        }
                      }
                    }}
                    className="flex-1 min-w-0 inline-flex items-center justify-center gap-0.5 py-1.5 rounded-lg border text-xs font-mono transition-all bg-void-black/50 border-gray-700 text-gray-400 hover:border-neon-cyan/50 hover:bg-neon-cyan/10 hover:text-neon-cyan"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {keyCount} <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  </motion.button>
                );
              })}
            </div>
          </div>
          
          {/* Preview + Faction */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl bg-void-black/50 border border-gray-700 mb-3">
              <span className="text-gray-400 text-sm font-rajdhani">You'll Get</span>
              <div className="text-right">
                <span className="font-orbitron text-lg text-neon-cyan">~{Number(estimatedKeys.post).toLocaleString()}</span>
                <span className="text-xs text-gray-500 ml-1">keys</span>
                {estimatedKeys.bonus && <span className="text-xs text-neon-cyan ml-2 animate-pulse">+5%!</span>}
              </div>
            </div>
            {/* Faction grid - 2 rows x 4 cols */}
            <div className="grid grid-cols-4 gap-2">
              {FACTIONS.map((faction) => {
                const isLocked = lockedFaction !== null && lockedFaction !== faction.contractId;
                const isSelected = selectedFaction === faction.contractId;
                return (
                  <motion.button
                    key={faction.id}
                    onClick={() => !isLocked && chooseFaction(faction.contractId)}
                    disabled={isLocked}
                    className={`flex flex-col items-center justify-center rounded-lg transition-all p-1.5 h-16 ${
                      isLocked ? 'opacity-30 cursor-not-allowed' : 
                      isSelected ? 'bg-neon-cyan/20 border-2 border-neon-cyan shadow-[0_0_15px_rgba(0,255,255,0.4)]' : 'bg-void-black/50 border border-transparent hover:border-gray-600'
                    }`}
                    whileHover={!isLocked ? { scale: 1.05 } : {}}
                    whileTap={!isLocked ? { scale: 0.95 } : {}}
                    title={`${faction.name} - ${faction.power}`}
                  >
                    <div className="relative">
                      <img 
                        src={faction.icon} 
                        alt={faction.name} 
                        className={`w-10 h-10 object-contain ${isLocked ? 'grayscale' : ''}`}
                        loading="lazy"
                        style={{ filter: isLocked ? 'brightness(0.4) grayscale(1)' : isSelected ? 'brightness(1.2) drop-shadow(0 0 8px rgba(0, 255, 255, 0.6))' : 'brightness(0.9)' }}
                      />
                      {isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-gray-500 text-lg">🔒</span>
                        </div>
                      )}
                      {isSelected && lockedFaction === faction.contractId && (
                        <div className="absolute -top-1 -right-1 bg-neon-cyan rounded-full w-4 h-4 flex items-center justify-center">
                          <span className="text-[8px]">✓</span>
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] mt-0.5 font-mono ${isLocked ? 'text-gray-600' : 'text-gray-400'}`}>
                      {isLocked ? '🔒' : faction.name}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            {/* Faction power info */}
            {selectedFaction > 0 && (
              <div className="mt-2 p-2 rounded-lg bg-void-black/30 border border-green-700/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neon-cyan font-mono">{getFactionByContractId(selectedFaction)?.power}</span>
                </div>
                <p className="text-[10px] text-green-400/70 mt-1 italic flex items-center gap-1">
                  <THALIAInline /> "{getFactionByContractId(selectedFaction)?.thaliaQuote}"
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Referrer input */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="Referrer address (optional)"
            value={referrer}
            onChange={(e) => setReferrer(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-void-black/50 border border-gray-700 text-white text-sm font-mono placeholder:text-gray-600 focus:outline-none focus:border-neon-purple focus:shadow-[0_0_15px_rgba(153,50,204,0.2)] transition-all"
          />
        </div>

        {/* Purchase message toggle & input */}
        <div className="mb-4">
          <button
            onClick={() => setShowMessageInput(!showMessageInput)}
            className="text-xs text-gray-500 hover:text-neon-cyan transition-colors flex items-center gap-1"
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3">
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {showMessageInput ? 'Hide message' : 'Add message to purchase'}
          </button>
          {showMessageInput && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2"
            >
              <input
                type="text"
                placeholder="Your message (shows in activity feed)"
                value={purchaseMessage}
                onChange={(e) => setPurchaseMessage(e.target.value.slice(0, 100))}
                maxLength={100}
                className="w-full px-3 py-2 rounded-lg bg-void-black/50 border border-neon-cyan/30 text-white text-sm font-rajdhani placeholder:text-gray-600 focus:outline-none focus:border-neon-cyan focus:shadow-[0_0_15px_rgba(0,255,255,0.2)] transition-all"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>💬 Message will be stored on-chain forever!</span>
                <span>{purchaseMessage.length}/100</span>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* CASCADE indicator */}
        {cascadeLevel > 0 && (
          <div className="mb-3 p-2 rounded-lg bg-neon-purple/10 border border-neon-purple/30 flex items-center justify-between">
            <span className="text-sm text-neon-purple font-rajdhani flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="currentColor"/></svg>
              CASCADE x{cascadeLevel}
            </span>
            <span className="text-xs text-gray-400">+{Math.min(cascadeLevel * 5, 15)}% variance</span>
          </div>
        )}
        
        {/* Low ETH warning - gas fees */}
        {account && ethBalance !== null && ethBalance < ethers.parseEther('0.001') && (
          <div className="mb-3 p-2 rounded-lg bg-amber-900/30 border border-amber-500/50 flex items-center gap-2">
            <span className="text-amber-400 text-base">⚠️</span>
            <div className="flex-1">
              <span className="text-xs text-amber-300 font-rajdhani">
                Low ETH balance ({parseFloat(ethers.formatEther(ethBalance)).toFixed(5)} ETH)
              </span>
              <p className="text-[10px] text-amber-400/70 mt-0.5">
                You need ETH for gas fees. Transactions may fail without sufficient ETH.
              </p>
            </div>
          </div>
        )}
        
        {/* Large purchase MEV warning */}
        {estimatedKeys.post > 1000n && (
          <div className="mb-3 p-2 rounded-lg bg-blue-900/30 border border-blue-500/50 flex items-center gap-2">
            <span className="text-blue-400 text-base">🤖</span>
            <div className="flex-1">
              <span className="text-xs text-blue-300 font-rajdhani">Large Purchase Detected</span>
              <p className="text-[10px] text-blue-400/70 mt-0.5">
                Large transactions may be targeted by MEV bots. Consider splitting into smaller purchases for better prices.
              </p>
            </div>
          </div>
        )}
        
        {/* Contextual game state warnings */}
        <BuyKeysWarnings
          isFOMOSurge={fomoSurgeActive}
          isFlashCrash={flashCrashActive}
          isAssassinMode={isAssassinMode}
          timeLeft={timeRemaining}
          isCursed={cascadeLevel > 0}
        />
        
        {/* 15s buy-lock (contract TIMESTAMP_BUFFER) */}
        {timeRemaining >= 0 && timeRemaining <= BUY_LOCK_SECONDS + 15 && (
          <div role="alert" className={`mb-3 p-3 rounded-xl border text-sm font-rajdhani ${timeRemaining <= BUY_LOCK_SECONDS ? 'border-red-500 bg-red-500/15 text-red-300' : 'border-amber-400 bg-amber-400/10 text-amber-300'}`}>
            {timeRemaining <= BUY_LOCK_SECONDS
              ? `⛔ Buys are locked in the final ${BUY_LOCK_SECONDS} seconds — the contract will reject them.`
              : `⚠️ Buys lock at ${BUY_LOCK_SECONDS}s left. You have ${timeRemaining - BUY_LOCK_SECONDS}s to get a transaction mined.`}
          </div>
        )}

        {/* Buy Button */}
        <motion.button
          onClick={needsApproval && !demoMode.isDemo ? () => void handleApprove() : handleBuy}
          disabled={isLoading || isApproving || (!account && !demoMode.isDemo) || (!demoMode.isDemo && timeRemaining >= 0 && timeRemaining <= BUY_LOCK_SECONDS)}
          className={`w-full py-4 rounded-xl font-orbitron text-lg font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            !account && !demoMode.isDemo
              ? 'bg-gray-800 text-gray-500' 
              : needsApproval && !demoMode.isDemo
                ? 'bg-gradient-to-r from-neon-purple to-neon-magenta text-white' 
                : 'btn-buy'
          }`}
          whileHover={account || demoMode.isDemo ? { scale: 1.01 } : {}}
          whileTap={account || demoMode.isDemo ? { scale: 0.99 } : {}}
        >
          {demoMode.isDemo ? (isLoading ? 'Processing...' : '🎮 Buy Demo Keys') : (!account ? 'Connect Wallet' : isLoading ? 'Processing...' : isApproving ? 'Approving...' : needsApproval ? 'Step 1 of 2: Allow test FRAME' : 'Buy keys')}
        </motion.button>
        {account && !demoMode.isDemo && needsApproval && (
          <div className="mt-2 text-xs text-gray-400 font-rajdhani leading-snug">
            Your wallet first asks to let the game contract use test FRAME for this buy (exact amount plus 2% for price movement). Then you confirm the buy.
            <label className="flex items-center gap-2 mt-1 cursor-pointer">
              <input type="checkbox" checked={approveUnlimited} onChange={e => setApproveUnlimited(e.target.checked)} />
              <span>Allow unlimited (skip this step next time; you can revoke it in your wallet)</span>
            </label>
          </div>
        )}
        
        {/* Reaper key (cuts 30s; separate from normal buys) */}
        <ReaperKey account={account} timeRemaining={timeRemaining} allowance={allowance} isApproving={isApproving} onApprove={handleApprove} onDone={() => { fetchData(); onPurchase?.(); }} demo={demoMode.isDemo} emptyRound={emptyRound} />

        {/* Post-purchase share prompt */}
        {lastPurchaseData && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 p-3 rounded-xl bg-neon-cyan/5 border border-neon-cyan/20 flex items-center justify-between gap-3"
          >
            <span className="text-xs text-gray-400 font-rajdhani">🔑 {lastPurchaseData.keys} keys acquired!</span>
            <SocialSharing 
              data={{ type: 'purchase', keys: lastPurchaseData.keys, amount: lastPurchaseData.amount }}
              playerAddress={account || undefined}
              className="flex-shrink-0"
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

